<script setup lang="ts">
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
const Error = defineAsyncComponent(() => import("@/components/Error.vue"));
const NotFound = defineAsyncComponent(() => import("@/pages/NotFound.vue"));

const { t } = useI18n();
const recent = useRecentStore();
const route = useRoute();
const mediaStore = useMediaPlayerStore();
const translations = useTranslationStore();
const store = useSongStore();
const appStore = useAppStore();
const r = useRoute();
const songNumber = computed(() => r.params.songNumber as string);
const numberRegex = /^[0-9]+$/;
const validSongNumber = computed(() => numberRegex.test(songNumber.value));
const fullPath = computed(() => r.fullPath as string);
const status = ref<LoadStatus>(LoadStatus.Loading);
const slug = computed(() => r.params.slug as string);
const translationCode = computed(() => {
  return translations.currentLanguageCode;
});

async function updatePageTitleAndDescription() {
  const authors = store.song?.authors.map((i) => i.label).join(", ");
  const artist = store.song?.artist;
  const title = store.song?.title;
  let pageTitle = "";
  let description = "";

  if (fullPath.value.includes('/viewlyrics')) {
    pageTitle = artist ? t("S.LIT_LyricsPageTitle", [title, artist]) : t("S.LIT_LyricsPageTitle", [title, authors]);
    description = artist ? t("S.LIT_PageDescriptionSEO_LyricsForTitleByAuthor",[title, artist]) : t("S.LIT_PageDescriptionSEO_LyricsForTitleByAuthor",[title,authors]);
  } else if (fullPath.value.includes('/viewchordsheet')) {
    pageTitle = artist ? t("S.LIT_ChordSheetPageTitle", [title, artist]) : t("S.LIT_ChordSheetPageTitle", [title, authors]);
    description = artist ? t("S.LIT_PageDescriptionSEO_ChordsForTitleByAuthor",[title, artist]) : t("S.LIT_PageDescriptionSEO_ChordsForTitleByAuthor",[title,authors]);
  } else if (fullPath.value.includes('/viewleadsheet')) {
    pageTitle = artist ? t("S.LIT_LeadSheetPageTitle", [title, artist]) : t("S.LIT_LeadSheetPageTitle", [title, authors]);
    description = artist ? t("S.LIT_PageDescriptionSEO_LeadSheetForTitleByAuthor",[title, artist]) : t("S.LIT_PageDescriptionSEO_LeadSheetForTitleByAuthor",[title,authors]);
  } else if (fullPath.value.includes('/viewvocalsheet')) {
    pageTitle = artist ? t("S.LIT_VocalSheetPageTitle", [title, artist]) : t("S.LIT_VocalSheetPageTitle", [title, authors]);
    description = artist ? t("S.LIT_PageDescriptionSEO_VocalSheetForTitleByAuthor",[title, artist]) : t("S.LIT_PageDescriptionSEO_VocalSheetForTitleByAuthor",[title,authors]);
  } else {
    updatePageTitle();
    return;
  }
  appStore.updateTitleAndDescription({
    title: pageTitle,
    description: description,
  });
}

function updatePageTitle() {
  const { title, artist, products } = store.song || {};
  const hasLyrics = products?.lyrics.exists;
  const hasChords = products?.chords.exists;
  const hasSheet = products?.lead.exists || products?.vocal.exists;
  let pageTitle = "";

  if (!title) {
    pageTitle = t("S.PageTabTitle");
  } else {
    if (artist) {
      pageTitle = `${title} | ${t("S.LIT_PageTitleSEO_ChordsLyricsSheetMusicByArtist", [artist])}`;
    } else {
      const titleKey = hasChords
        ? hasLyrics
          ? hasSheet
            ? t("S.LIT_PageTitleSEO_SongWithChordsLyricsAndSheetMusic")
            : t("S.LIT_PageTitleSEO_SongWithChordsAndLyrics")
          : hasSheet
          ? t("S.LIT_PageTitleSEO_SongWithChordsAndSheetMusic")
          : t("S.LIT_PageTitleSEO_SongWithChords")
        : hasLyrics
        ? hasSheet
          ? t("S.LIT_PageTitleSEO_SongWithLyricsAndSheetMusic")
          : t("S.LIT_PageTitleSEO_SongWithLyrics")
        : t("S.LIT_PageTitleSEO_SongWithSheetMusic");

      pageTitle = `${title}  ${titleKey}`;
    }
  }
  appStore.updateTitle(pageTitle);
}

watch(songNumber, loadSong);
watch(slug, loadSong);
watch(fullPath, updatePageTitleAndDescription)
watch(translationCode, () => {
  setTimeout(() => {
    updatePageTitleAndDescription();
  }, 50)
})

async function loadSong() {
  if (!validSongNumber.value) {
    return;
  }

  recent.onViewSongNumber(songNumber.value);
  await store.getSongDetails(songNumber.value, slug.value);
  store.updateSongdetailsMeta();
  if (!r.params.slug && store.song?.slug && songNumber.value) {
    router.replace(`/songs/${songNumber.value}/${store.song.slug}`);
  }

  (window as any).dataLayer.push({
    song_number: songNumber.value,
  });
  updatePageTitleAndDescription()
}

onMounted(() => {
  try {
    loadSong();
    status.value = LoadStatus.Ready;
  } catch {
    status.value = LoadStatus.Error;
  }
});

onBeforeUnmount(() => {
  pausePlayingMusic();
  store.song = null;
});

function pausePlayingMusic() {
  mediaStore.pause();
  if (
    mediaStore.status !== MediaPlayerStatus.Unloaded &&
    mediaStore.status !== MediaPlayerStatus.Paused
  ) {
    mediaStore.pause();
  }
}
watch(songNumber, pausePlayingMusic);
onDeactivated(pausePlayingMusic);

const beta = useBetaStore();
const enableRecordingFocus = computed(() => beta.features.recordingFocusLayout);
</script>

<template>
  <NotFound v-if="store.songNotFound || !validSongNumber" />
  <Error v-else-if="store.status === LoadStatus.Error" />
  <div v-else>
    <main>
      <AboveFoldSplashRecordingFocused
        v-if="enableRecordingFocus && store.song?.enableSongVersion"
      />
      <AboveFoldSplash v-else />

      <div class="below-splash">
        <Container class="-mb-5">
          <NavProducts />
        </Container>
        <RouterView v-slot="{ Component, route }">
          <component
            :is="Component"
            :key="route.path"
          />
        </RouterView>
      </div>
    </main>
    <!-- <DiscoverMoreSongs class="d-none" /> -->
  </div>
</template>

<style scoped lang="postcss"></style>
